import {calculateDistance} from "../../../travian/Calculator/distance.js";
import {calculateTravelTime} from "../../../travian/Calculator/travelTime.js";
import {findTroopThresholds} from "./TravianHelper.js"
import {linkToSendAttack} from "../../../travian/links.js";

export function decorateOasisData(fromX, fromY, oasisData) {
    const toX = oasisData.x, toY = oasisData.y;
    const oasisTroops = Array(10)
        .fill(0)
        .map((_, i) => oasisData['t' + (i + 1)]);

    const distance = calculateDistance(fromX, fromY, toX, toY);
    const duration = calculateTravelTime({
        distance,
        baseSpeed: 16,
        tournamentSquare: 7
    });

    // Check data for 0-10 losses to find "best fit"
    const attacker = {
        unitType: 64,
        unitAmount: 1000,
        unitLevel: 20,
    };
    const oasisUnits = oasisTroops
        .map((amount, key) => ({
            unitType: 31 + key,
            unitAmount: amount,
        }))
        .filter(k => k.unitAmount > 0);

    const possibleLosses = [
        0, 1, 2, 3, 4, 5, 6, 7, 8, 9,
        10, 12, 14, 16, 18, 20, 22, 24, 27, 30,
        35, 40, 45, 50, 55, 60, 65, 70, 75
    ];
    const results = findTroopThresholds(attacker, oasisUnits, false, possibleLosses);

    const totalBounty = oasisData.bounty.reduce((a, b) => a + b, 0);
    const totalHeroBounty = oasisData.heroBounty.reduce((a, b) => a + b, 0);

    let possibleRespawnPenalty = 0;
    if (oasisData.lastAttack < oasisData.time - 6 * 3600 && totalHeroBounty < 160 * 8) {
        possibleRespawnPenalty = 0.95;
    }

    let bestProfitPUS = null, bestProfitPUT = null, hasPositiveOutcome = false;

    for (const losses in results) {
        let units = results[losses].units;
        const actualLosses = results[losses].losses;
        /** @type {number|null} */
        const nextPossibleLosses = possibleLosses[possibleLosses.indexOf(parseInt(losses)) + 1] ?? null;
        if (units > 2999 || units === null) {
            continue;
        }
        units++; // safety buffer of 1 unit
        if ((units - actualLosses) * 75 < totalBounty && actualLosses < 10) {
            if (nextPossibleLosses !== null && (results[nextPossibleLosses] - results[nextPossibleLosses].losses) * 75 < totalBounty) {
                continue;
            } else {
                units = Math.ceil(totalBounty / 75) + parseInt(actualLosses);
            }
        }
        const gain = totalBounty + totalHeroBounty;
        if (gain === 0) {
            continue;
        }
        const loss = actualLosses * 895;
        const profit = gain - loss;
        const lossRatio = loss / gain;
        const normalizedGain = Math.max(0, gain - 4000) / 46000;
        const desiredLossRatio = Math.min(0.4, Math.max(0.2, 0.15 + (normalizedGain ** 2) * 0.8));
        if (lossRatio > desiredLossRatio) {
            continue;
        }
        if (profit > 0) {
            hasPositiveOutcome = true;
        }
        const profitPerUnitSent = profit / units * (1 - possibleRespawnPenalty);
        const profitPerUnitTime = profitPerUnitSent / (duration / 3600) * (1 - possibleRespawnPenalty);
        if (bestProfitPUT === null || bestProfitPUT.score < profitPerUnitTime || (bestProfitPUT.score === profitPerUnitTime && bestProfitPUT.gain < gain)) {
            bestProfitPUT = {score: profitPerUnitTime, units, losses, gain, loss, lossRatio, desiredLossRatio, actualLosses};
        }
        if (bestProfitPUS === null || bestProfitPUS.score < profitPerUnitSent || (bestProfitPUS.score === profitPerUnitSent && bestProfitPUS.gain < gain)) {
            bestProfitPUS = {score: profitPerUnitSent, units, losses, gain, loss, lossRatio, desiredLossRatio, actualLosses};
        }
    }

    const lastScanThreshold = Date.now() / 1000 - 1.5 * 3600;
    const lastAttackThreshold = Date.now() / 1000 - 4 * 3600;
    const lastAttackSentThreshold = Date.now() / 1000 - 7 * 3600;
    const manyAnimalsThreshold = Date.now() / 1000 - 36 * 3600;

    const needsScan = oasisData.time < lastScanThreshold
        && (oasisData.lastAttackSent === null || oasisData.lastAttackSent < lastAttackSentThreshold)
        && (oasisData.lastAttack === null || oasisData.lastAttack < lastAttackThreshold)
        && (oasisData.time < manyAnimalsThreshold || oasisTroops.reduce((a, b) => a + b, 0) < 150 || oasisData.lastAttackSent > oasisData.time);

    const linkToAttack = linkToSendAttack({
        targetX: oasisData.x,
        targetY: oasisData.y,
        sourceVillageId: 22189,
        troops: {
            t4: bestProfitPUT?.units ?? 0
        },
        eventType: 4
    });

    return {
        ...oasisData,
        bestProfitPUT,
        bestProfitPUS,
        hasPositiveOutcome,
        duration,
        distance,
        needsScan,
        linkToAttack,
    };
}
